import { Client } from "@/Common/Api/Client";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { PatientSearchMatchingPopup } from "@/Common/Components/Patient/PatientSearchMatching/PatientSearchMatchingPopup";
import { useLocalSlice } from "@/Common/CustomHooks/useLocalSlice";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { PatientMergeEligibilityStatus } from "@/Patient/Models/PatientMergeRequest/PatientMergeEligibilityStatus";
import { PatientMergeRequestErrorPopup } from "./PatientMergeRequestErrorPopup";
import { PatientMergeRequestFinalConfirmationPopup } from "./PatientMergeRequestFinalConfirmationPopup";
import { WarningPopup } from "./PatientMergeRequestWarningPopup";
import { WarningPopupToContinue } from "./PatientMergeRequestWarningPopupToContinue";

interface IPatientMergeRequestControllerProps {
    onClose: () => void;
    mainPatientContext: Patient;
}

interface PatientMergeRequestControllerState {
    mainPatient: Patient;
    selectedPatient: Person | null,
    messageKey?: string;
    status?: PatientMergeEligibilityStatus;
    showErrorPopup: boolean;
    showWarningPopup: boolean;
    showFinalConfirmationPopup: boolean;
    switchPatients: boolean;
}

const cantMergedList = new Set([
    PatientMergeEligibilityStatus.BothPatientsHavePortal,
    PatientMergeEligibilityStatus.BothPatientsAreReadonly
]);

function hasFlag(permissions: number, flag: PatientMergeEligibilityStatus): boolean {
    return (permissions & flag) === flag;
}

/**
 *
 * @param mergeToPatient Patient A or the current patient tab.
 * @param mergeFromPatient Patient B or the selected patient
 * @returns The status of the eligibility of the merge. (This is is a flag type);
 */
function getPatientMergeEligibilityStatus(mergeToPatient: Patient, mergeFromPatient: Person): PatientMergeEligibilityStatus {
    let status = PatientMergeEligibilityStatus.None;
    if (mergeToPatient.IsRegisteredToPortal())
        status |= PatientMergeEligibilityStatus.MergeToPatientHasPortal;
    if (Person.IsRegisteredToPortal(mergeFromPatient))
        status |= PatientMergeEligibilityStatus.MergeFromPatientHasPortal;
    if (mergeToPatient.IsReadOnly)
        status |= PatientMergeEligibilityStatus.MergeToPatientIsReadOnly;
    if (mergeFromPatient.IsReadOnly)
        status |= PatientMergeEligibilityStatus.MergeFromPatientIsReadOnly;

    return status;
}

// Return type is too complexe to define explicitly
// eslint-disable-next-line
function usePatientMergeRequestControllerState(initialState: Readonly<PatientMergeRequestControllerState>) {
    return useLocalSlice({
        initialState: initialState,
        reducers: {
            setSelectedPatient: (state: PatientMergeRequestControllerState, action: { payload: Person | null }) => {
                if (state.selectedPatient && state.selectedPatient.ContactId === state.mainPatient.IndividualResponse.Contact_id)
                    return;

                state.selectedPatient = action.payload;
                state.messageKey = null;
                state.showErrorPopup = false;
                state.showWarningPopup = false;
                state.status = null;
                state.switchPatients = false;

                if (!state.selectedPatient)
                    return;

                state.status = getPatientMergeEligibilityStatus(state.mainPatient, state.selectedPatient);

                if (hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsHavePortal)) {
                    state.messageKey = "PatientMergeRequestPopup_BothPatientsHavePortalAccountErrorMessage";
                }
                else if (hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsAreReadonly)) {
                    state.messageKey = "PatientMergeRequestPopup_BothPatientsAreReadOnlyErrorMessage";
                    state.showErrorPopup = true;
                }
            },
            showPatientSwitchWarningPopup: state => {
                if (!state.selectedPatient)
                    return;

                if (hasFlag(state.status, PatientMergeEligibilityStatus.MergeFromPatientIsReadOnly)) {
                    state.messageKey = "PatientMergeErrorMessage_PatientBIsReadOnlyAndPatientAIsNotWarningMessage";
                    state.showWarningPopup = true;
                    state.switchPatients = true;
                }
            },
            closeErrorWarningPopups: (state: PatientMergeRequestControllerState) => {
                state.showErrorPopup = false;
                state.showWarningPopup = false;
                state.showFinalConfirmationPopup = false;
            },
            showFinalConfirmationPopup: state => {
                state.showErrorPopup = false;
                state.showWarningPopup = false;
                state.showFinalConfirmationPopup = true;
            }
        }
    });
}

function isActionButtonDisabled(state: Readonly<PatientMergeRequestControllerState>): boolean {
    return !state.mainPatient
        || !state.selectedPatient
        || state.mainPatient.IndividualResponse?.Contact_id === state.selectedPatient.ContactId
        || hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsAreReadonly)
        || hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsHavePortal);
}

async function savePatientMergeRequest(mergeToPatientContactId: string, mergeFromPatientContactId: string): Promise<void> {
    const response = await Client.postAsync<GenericResponse>(ServerOperations.Server.Myle_Patient_SaveMergeRequest, {
        MergeToPatientContactId: mergeToPatientContactId,
        MergeFromPatientContactId: mergeFromPatientContactId
    });

    if (!response.isSuccess) {
        sessionStore.showPopup(response.errors);
        return;
    }

    sessionStore.dispatch(LocalPatientOperations.Profile.UpdatePatientMergeStatus, { mergeToPatientContactId, mergeFromPatientContactId });
}

function PatientMergeRequestController(props: Readonly<IPatientMergeRequestControllerProps>): JSX.Element {
    const [state, dispatch] = usePatientMergeRequestControllerState({
        mainPatient: props.mainPatientContext,
        selectedPatient: null,
        messageKey: null,
        status: null,
        showErrorPopup: false,
        showWarningPopup: false,
        showFinalConfirmationPopup: false,
        switchPatients: false
    });

    const { getText } = useTranslation();

    return (
        <>
            <PatientSearchMatchingPopup
                title={getText("MergePatientLabel")}
                currentPatient={props.mainPatientContext}
                error={hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsHavePortal) && state.messageKey
                    ? getText(state.messageKey)
                    : null
                }
                onSelect={patient => dispatch.setSelectedPatient(patient)}
                disableActionButton={isActionButtonDisabled(state)}
                onActionButtonClick={() => hasFlag(state.status, PatientMergeEligibilityStatus.MergeFromPatientIsReadOnly) ? dispatch.showPatientSwitchWarningPopup() : dispatch.showFinalConfirmationPopup()}
                actionButtonLabel={getText("MergePatientsLabel")}
                onClose={() => props.onClose()}
                ignorePatientsAlreadyPartOfMergeRequest
            />
            {hasFlag(state.status, PatientMergeEligibilityStatus.BothPatientsAreReadonly) && state.showErrorPopup && (
                <PatientMergeRequestErrorPopup
                    errorMessageKey={state.messageKey}
                    onClose={() => dispatch.closeErrorWarningPopups()}
                />
            )}
            {state.showWarningPopup && cantMergedList.has(state.status) && (
                <WarningPopup
                    errorMessageKey={state.messageKey}
                    onClose={() => dispatch.closeErrorWarningPopups()}
                    patientA={Person.FromPatient(state.mainPatient)}
                    patientB={state.selectedPatient}
                />
            )}
            {state.showWarningPopup && hasFlag(state.status, PatientMergeEligibilityStatus.MergeFromPatientIsReadOnly) && (
                <WarningPopupToContinue
                    warningMessageKey={state.messageKey}
                    onClose={() => dispatch.closeErrorWarningPopups()}
                    patientA={Person.FromPatient(state.mainPatient)}
                    patientB={state.selectedPatient}
                    onContinue={() => dispatch.showFinalConfirmationPopup()}
                />
            )}
            {state.showFinalConfirmationPopup && (
                <PatientMergeRequestFinalConfirmationPopup
                    patientA={state.switchPatients ? state.selectedPatient : Person.FromPatient(state.mainPatient)}
                    patientB={state.switchPatients ? Person.FromPatient(state.mainPatient) : state.selectedPatient}
                    onClose={() => props.onClose()}
                    onBack={() => dispatch.closeErrorWarningPopups()}
                    onConfirm={(patientAContactId, patientBContactId) => savePatientMergeRequest(patientAContactId, patientBContactId)}
                />
            )}
        </>
    );
}

export { isActionButtonDisabled as GetPatientMergeActionButtonDisabled, getPatientMergeEligibilityStatus, PatientMergeRequestController, usePatientMergeRequestControllerState };
